import { fr } from "@codegouvfr/react-dsfr";
import { Button } from "@codegouvfr/react-dsfr/Button";
import { Input } from "@codegouvfr/react-dsfr/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { tss } from "tss-react/dsfr";
import { z } from "zod";

const parcelleSchema = z.object({
  codeInsee: z.string().regex(/^\d{5}$/, "Code INSEE (5 chiffres)"),
  prefix: z.string().regex(/^\d{3}$/, "Préfixe (3 chiffres)"),
  section: z
    .string()
    .regex(
      /^[A-Z0-9]{2}$/,
      "Section (2 caractères : lettres majuscules et/ou chiffres)"
    ),
  numero: z.string().regex(/^\d{4}$/, "Numéro (4 chiffres)"),
});

type ParcelleFormData = z.infer<typeof parcelleSchema>;

interface ParcelleSearchProps {
  onParcelleRequested: (
    response: { data?: any; error?: any },
    values: ParcelleFormData
  ) => void;
  onChange?: () => void;
  formValues?: ParcelleFormData;
  autoSubmit?: boolean;
}

const ParcelleSearch = ({
  onParcelleRequested,
  onChange,
  formValues,
  autoSubmit,
}: ParcelleSearchProps) => {
  const { cx, classes } = useStyles();

  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<ParcelleFormData>({
    resolver: zodResolver(parcelleSchema),
    mode: "all",
    defaultValues: {
      prefix: "000",
      ...formValues,
    },
  });

  const onSubmit = async (data: ParcelleFormData) => {
    const { codeInsee, section, numero, prefix } = data;
    const url = `https://apicarto.ign.fr/api/cadastre/parcelle?${prefix !== "000" ? `com_abs=${prefix}` : `code_insee=${codeInsee}`
      }&section=${section}&numero=${numero}`;

    setIsLoading(true);
    try {
      const response = await fetch(url);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const responseData = await response.json();
      onParcelleRequested({ data: responseData }, data);
    } catch (error) {
      onParcelleRequested({ error }, data);
    }
    setIsLoading(false);
  };

  const handleInputChange = (
    field: any,
    fieldName: keyof ParcelleFormData,
    value: string
  ) => {
    let formattedValue = value;
    if (fieldName === "prefix" || fieldName === "numero") {
      const valueMaxLength = fieldName === "prefix" ? 3 : 4;
      const digits =
        value.length > valueMaxLength
          ? value.replace(/\D/g, "").slice(value.length - valueMaxLength)
          : value;
      formattedValue = digits.padStart(valueMaxLength, "0");
    } else if (fieldName === "section") {
      formattedValue = value.toUpperCase().slice(0, 2);
    }
    field.onChange(formattedValue);
  };

  useEffect(() => {
    if (formValues) {
      Object.entries(formValues).forEach(([key, value]) => {
        setValue(key as keyof ParcelleFormData, value);
      });
    }
    trigger();
  }, [formValues, setValue, trigger]);

  const hasAutoSubmittedRef = useRef(false);
  useEffect(() => {
    if (autoSubmit && isValid && !isLoading && !hasAutoSubmittedRef.current) {
      hasAutoSubmittedRef.current = true;
      handleSubmit(onSubmit)();
    }
  }, [autoSubmit, isValid, isLoading, handleSubmit]);

  return (
    <div className={cx(classes.container)}>
      <form onSubmit={handleSubmit(onSubmit)} onChange={onChange}>
        {Object.entries(fieldsConfig).map(([name, config]) => (
          <Controller
            key={name}
            name={name as keyof ParcelleFormData}
            control={control}
            render={({ field }) => (
              <Input
                label={config.label}
                state={
                  errors[name as keyof ParcelleFormData] ? "info" : "success"
                }
                hintText={config.hintText}
                stateRelatedMessage={config.patternText}
                nativeInputProps={{
                  ...config.nativeInputProps,
                  value: field.value || "",
                  onChange: (e) => {
                    const fieldName = name as keyof ParcelleFormData;
                    if (
                      fieldName === "prefix" ||
                      fieldName === "numero" ||
                      fieldName === "section"
                    ) {
                      handleInputChange(field, fieldName, e.target.value);
                    } else {
                      field.onChange(e.target.value);
                    }
                  },
                  onBlur: field.onBlur,
                  name: field.name,
                }}
              />
            )}
          />
        ))}

        <Button type="submit" disabled={!isValid || isLoading}>
          {isLoading ? (
            <i className={fr.cx("ri-loader-4-line")} />
          ) : (
            "Rechercher"
          )}
        </Button>
      </form>
    </div>
  );
};

const fieldsConfig: Record<
  string,
  {
    label: string;
    hintText: string;
    patternText: string;
    nativeInputProps: Partial<React.InputHTMLAttributes<HTMLInputElement>>;
  }
> = {
  codeInsee: {
    label: "Code INSEE",
    hintText: "Saisissez le code INSEE de la commune",
    patternText: "Exemple : 44000, 75001",
    nativeInputProps: {
      inputMode: "numeric",
      pattern: "\\d{5}",
      type: "text",
    },
  },
  prefix: {
    label: "Préfixe",
    patternText: "Exemple : 000, 001",
    hintText: "Saisissez les références de préfixe",
    nativeInputProps: {
      inputMode: "numeric",
      pattern: "\\d{3}",
      type: "text",
    },
  },
  section: {
    label: "Numéro de section",
    patternText: "Saisir une référence : AO, AA...",
    hintText: "Saisissez les références de section",
    nativeInputProps: {
      pattern: "[A-Z0-9]{2}",
      type: "text",
    },
  },
  numero: {
    label: "Numéro de parcelle",
    hintText: "Saisissez les références de parcelle",
    patternText: "Exemple : 0545, 0001",
    nativeInputProps: {
      inputMode: "numeric",
      pattern: "\\d{4}",
      type: "text",
    },
  },
};

const useStyles = tss.create(() => ({
  container: {
    form: {
      display: "flex",
      flexDirection: "row",
      gap: fr.spacing("6v"),
      alignItems: "center",
      button: {
        marginBottom: fr.spacing("1v"),
        flexGrow: 1,
        justifyContent: "center",
        i: {
          display: "inline-block",
          animation: "spin 1s linear infinite",
        },
      },
      ".fr-input-group": {
        "&::before": {
          backgroundImage: "none",
        },
        ".fr-label": {
          color: `${fr.colors.decisions.text.actionHigh.grey.default} !important`,
        },
      },
      [fr.breakpoints.down("md")]: {
        flexDirection: "column",
        alignItems: "start",
        gap: 0,
        button: {
          width: "100%",
          marginBottom: fr.spacing("6v"),
        },
        ".fr-input-group": {
          width: "100%",
        },
      },
    },
  },
}));

export default ParcelleSearch;
